import { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getStraightPath,
  useStore,
} from '@xyflow/react';
import { ReactComponent as TruckIcon } from 'assets/icons/car.svg';
import { ReactComponent as FactoryIcon } from 'assets/icons/factory.svg';
import { ReactComponent as PlaneIcon } from 'assets/icons/plane.svg';
import { ReactComponent as SeaIcon } from 'assets/icons/ship.svg';
import { ReactComponent as RailIcon } from 'assets/icons/train.svg';

import { VC_EDGE_SIMPLE_PATH_ZOOM_THRESHOLD } from '../../../../../../constants/general';
import {
  VALUE_CHAIN_CONNECTION_TYPES,
} from '../../../../../../constants/interfaces';

import type { TTransportMethod } from '../../../../../../constants/interfaces';
import type { ICustomEdge } from '../interfaces/ICustomEdge';

import '../sass/CustomEdge.scss';

function CustomEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: ICustomEdge) {
  // When the board is zoomed out far enough, draw cheap straight paths instead of beziers
  const isSimplePathZoom = useStore((state) => state.transform[2] <= VC_EDGE_SIMPLE_PATH_ZOOM_THRESHOLD);
  const isDraftReviewMode = Boolean(data?.isDraftReviewMode);
  const isDraftConnection = Boolean(data?.draft);
  const isScenarioViewMode = Boolean(data?.isScenarioViewMode);
  const isScenarioHighlighted = Boolean(data?.isScenarioHighlighted);
  const edgeOpacity = (() => {
    if (isScenarioViewMode) return isScenarioHighlighted ? 1 : 0.24;
    if (isDraftReviewMode && !isDraftConnection) return 0.24;
    return 1;
  })();
  const offsetSourceY = sourceY + (data?.sourceOffset || 0);
  const offsetTargetY = targetY + (data?.targetOffset || 0);

  // Apply pathOffset to create different curves and prevent overlapping
  const pathOffset = data?.pathOffset || 0;

  // Vary curvature based on pathOffset to create different curve angles
  const baseCurvature = 0.25;
  const curvatureVariation = pathOffset * 0.002;
  const dynamicCurvature = baseCurvature + curvatureVariation;

  const [edgePath, labelX, labelY] = isSimplePathZoom
    ? getStraightPath({
      sourceX,
      sourceY: offsetSourceY,
      targetX,
      targetY: offsetTargetY,
    })
    : getBezierPath({
      sourceX,
      sourceY: offsetSourceY,
      sourcePosition,
      targetX,
      targetY: offsetTargetY,
      targetPosition,
      curvature: dynamicCurvature,
    });

  // Apply the same offset to label position to prevent icon overlap
  const averageOffset = ((data?.sourceOffset || 0) + (data?.targetOffset || 0)) / 2;
  const offsetLabelY = labelY + averageOffset;

  const transportMethodIcons: Record<TTransportMethod, React.JSX.Element> = {
    truck: <TruckIcon width={20} height={20} />,
    air: <PlaneIcon width={20} height={20} />,
    sea: <SeaIcon width={20} height={20} />,
    rail: <RailIcon width={20} height={20} />,
  };

  const renderIcons = () => data?.connection?.transportMethods
    ?.filter((method) => Object.keys(transportMethodIcons).includes(method))
    .map((method) => (
      <div className="button-edge__icon" key={method}>
        {transportMethodIcons[method]}
      </div>
    )) ?? null;

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={`url('#edge-marker-${data?.connectionType}')`}
        className={`button-edge__connectionLine ${data?.connectionType}`}
        style={{ opacity: edgeOpacity }}
      />
      <EdgeLabelRenderer>
        <div
          className="button-edge__wrapper"
          style={{
            opacity: edgeOpacity,
            transform: `translate(-50%, -50%) translate(${labelX}px,${offsetLabelY}px)`,
          }}
        >
          {data?.connectionType === VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS ? (
            renderIcons()
          ) : null}
          {data?.connectionType === VALUE_CHAIN_CONNECTION_TYPES.MANUFACTURE ? (
            <div className="button-edge__icon">
              <FactoryIcon width={20} height={20} />
            </div>
          ) : null}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default memo(CustomEdge);
